import React, { ReactElement } from 'react';

import ListItem from '@material-ui/core/ListItem';

import {
    IActionDataParams,
    IReport,
    IReportEntity,
} from '../../../state/types';

import ListItemEntityActions from '../../../components/report/ListItemEntityActions/ListItemEntityActions';
import EntityMetaIcon from '../../../components/IconWithTooltip';
import { useStyles } from './Themable.hooks';
import { useUserSettings } from '../../../state/user/index.hooks';
import valueFormatters from './utils/valueFormatters';
import ActionEntity from '../ActionEntity';
import TranslationHelper from '../../../helpers/TranslationHelper';

type TActionHandler =
    | ((type: 'add' | 'edit' | 'preview', params?: IActionDataParams) => void)
    | ((
          type: 'add' | 'edit' | 'preview' | 'internalLink',
          params: IActionDataParams,
          collectionId?: string
      ) => void);

interface IOwnProps {
    report: IReport;
    entity: IReportEntity;
    handleDelete?: (id: string, message?: string) => void;
    copyToClipboardHandler?: {
        action: (id?: string, value?: string) => void;
        label: () => string;
    };
    renderImageComponent: (previewUrl: string, downloadUrl: string) => void;
    editHandler?: TActionHandler;
    addHandler?: TActionHandler;
    previewHandler?: TActionHandler;
    customRender?: ReactElement;
    borderless?: boolean;
}

const ReportListItem = ({
    report,
    entity,
    handleDelete,
    copyToClipboardHandler,
    addHandler,
    editHandler,
    previewHandler,
    renderImageComponent,
    customRender,
    borderless,
}: IOwnProps) => {
    const classes = useStyles();

    const reportActionTypes = report?._meta?.actions;

    const {
        preview: previewAction,
        download: downloadAction,
        externalLink: externalLinkAction,
    } = entity.actions ?? {};

    const deleteHandler = reportActionTypes?.delete && handleDelete;
    const userSettings = useUserSettings();

    if (previewAction && downloadAction) {
        return (
            <>{renderImageComponent(previewAction.api, downloadAction.api)}</>
        );
    }

    const linkEntity =
        String(entity.value).startsWith('http') &&
        entity.type !== 'externalLink';

    const sourceSetEntity =
        (Array.isArray(entity.value) || typeof entity.value !== 'object') &&
        !externalLinkAction &&
        !linkEntity;

    const formatEntityValueToString = (entity: IReportEntity): string => {
        if (entity.value) {
            return entity.translateValue
                ? TranslationHelper.translate(
                      entity.value.toString(),
                      entity.translateValue
                  )
                : entity.value.toString();
        } else {
            return '';
        }
    };
    const formatEntity = (e: IReportEntity) => {
        const formatter = valueFormatters[entity.type];
        if (formatter) {
            return formatter(e, userSettings);
        } else {
            return formatEntityValueToString(e);
        }
    };

    const renderEntityValue = () => {
        if (!entity.label) {
            return <></>;
        }
        return sourceSetEntity ? (
            <span className={classes.itemValue}>
                {formatEntity(entity)}
                {customRender}
            </span>
        ) : (
            <ActionEntity entity={entity} customRender={customRender} />
        );
    };
    return (
        <ListItem
            className={
                borderless ? classes.borderlessListItem : classes.listItem
            }
        >
            <>
                <div className={classes.itemLabel}>
                    {entity.translatedLabel || entity.label || entity.value}
                    {entity._meta?.icon?.type === 'info' && (
                        <EntityMetaIcon
                            iconId={entity._meta.icon.id}
                            label={entity._meta.icon.label}
                        />
                    )}
                </div>

                <span className={classes.secondaryAction}>
                    {renderEntityValue()}
                    <ListItemEntityActions
                        editHandler={editHandler}
                        addHandler={addHandler}
                        deleteHandler={deleteHandler}
                        previewHandler={previewHandler}
                        copyToClipboardHandler={copyToClipboardHandler}
                        report={report}
                        entity={entity}
                    />
                </span>
            </>
        </ListItem>
    );
};

export default ReportListItem;
